import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import { ICp } from 'constants/cp.constants';
import { API_ENDPOINTS } from 'constants/db.constants';
import API from '../../services/api.service';
import _ from 'lodash';
import { getFormattedDate } from '../../utils/global.util';
import { timing } from 'constants/ui.constants';
import { formatCp } from '../../utils/cp.util';
import { AppErrorCodes, ErrorMessages, Model } from 'constants/errorMessages.constants';

export interface IStateError {
  errorCode: number | null;
  errorMessage: string;
}

interface ICpState {
  cps?: null | ICp[];
  cp?: null | ICp;
  loading: boolean;
  error?: IStateError;
  login: boolean;
}

const initialState: ICpState = {
  cps: undefined,
  cp: null,
  loading: false,
  error: undefined,
  login: false
};

export const getAllCps = createAsyncThunk(API_ENDPOINTS.CP.GET_ALL_CP, (data: any, { rejectWithValue }) => {
  return API.getInstance()
    .get(API_ENDPOINTS.CP.GET_ALL_CP, data)
    .then((response) => response.data)
    .catch((err) => rejectWithValue(err));
});

export const getCpById = createAsyncThunk(API_ENDPOINTS.CP.GET_CP_BY_ID, (id?: string) =>
  API.getInstance()
    .get(`${API_ENDPOINTS.CP.GET_CP_BY_ID}${id}`)
    .then((response) => response.data)
);

export const createNewCp = createAsyncThunk(API_ENDPOINTS.CP.CREATE_CP, async (data: any, { rejectWithValue }) => {
  try {
    const response = await API.getInstance().post(API_ENDPOINTS.CP.CREATE_CP, data.cp);
    return {
      data: response.data,
      extra: data.extra
    };
  } catch (err) {
    rejectWithValue(err);
  }
});

export const updateCp = createAsyncThunk(API_ENDPOINTS.CP.UPDATE_CP, async (data: any, { rejectWithValue }) => {
  const { cpId, updateCpData, siteName } = data;
  try {
    const response = await API.getInstance().put(`${API_ENDPOINTS.CP.UPDATE_CP}${cpId}`, updateCpData);
    return {
      data: response.data.data,
      extra: { siteName }
    };
  } catch (err: any) {
    return rejectWithValue(err.response?.data || err);
  }
});

export const cpSlice = createSlice({
  name: 'cp',
  initialState,
  reducers: {
    setError: (state, { payload }) => {
      if (!_.keys(payload).length) {
        state.error = undefined;
        return;
      }
      state.error = {
        errorCode: payload.errorCode,
        errorMessage: payload.data?.message
      };
    }
  },
  extraReducers: {
    [getAllCps.pending.type]: (state) => {
      state.loading = true;
      state.error = undefined;
    },
    [getAllCps.fulfilled.type]: (state, { payload }) => {
      // TODO use func
      state.cps = payload.data?.map((cp: any) => {
        return {
          id: cp.id,
          idNumber: cp.cp_identification,
          assignedSite: cp.name,
          country: cp.country,
          active: cp.active,
          lastActivity: getFormattedDate(cp?.last_used, timing.DATE) || getFormattedDate(cp?.created_at, timing.DATE),
          lastEdit: getFormattedDate(cp?.updated_at),
          createdAt: getFormattedDate(cp?.created_at),
          created_by_first_name: cp.created_by_first_name,
          created_by_last_name: cp.created_by_last_name,
          updated_by_first_name: cp.updated_by_first_name,
          updated_by_last_name: cp.updated_by_last_name
        };
      });
      state.loading = false;
      state.login = true;
    },
    [getAllCps.rejected.type]: (state, { payload }) => {
      state.loading = false;
      state.error = {
        errorCode: payload?.data?.statusCode,
        errorMessage: payload?.data?.message
      };
    },
    [createNewCp.pending.type]: (state) => {
      state.loading = true;
      state.error = undefined;
    },
    [createNewCp.fulfilled.type]: (state, { payload }) => {
      const { data, extra } = payload;
      const cp = formatCp({ ...data.data, ...extra });
      state.cps?.push(cp as ICp);
      state.loading = false;
    },
    [createNewCp.rejected.type]: (state, { payload }) => {
      state.loading = false;
      state.error = {
        errorCode: payload?.data?.statusCode,
        errorMessage: payload?.data?.message
      };
    },
    [updateCp.pending.type]: (state) => {
      state.loading = true;
      state.error = undefined;
    },
    [updateCp.fulfilled.type]: (state, { payload }) => {
      const { data, extra } = payload;
      const cps = [...current(state).cps!];
      const cpIndex = cps?.findIndex((cp) => cp.id === data.id);
      if (cpIndex >= 0) {
        cps![cpIndex] = {
          ...cps![cpIndex],
          active: data.active,
          assignedSite: extra.siteName,
          idNumber: data?.cp_identification || null
        };
        state.cps = cps;
      }
      state.loading = false;
    },

    [updateCp.rejected.type]: (state, { payload }) => {
      state.loading = false;
      state.error = {
        errorCode: payload?.data?.statusCode || payload.statusCode,
        //if the error is related to activation, show a specific message that we received from the backend (app_code=)
        errorMessage:
          payload?.data?.app_code === AppErrorCodes.ACTIVATION_ERROR
            ? ErrorMessages[AppErrorCodes.ACTIVATION_ERROR](Model.CP)
            : 'Update failed. Please try again later.'
      };
    }
  }
});

export const { setError } = cpSlice.actions;

export default cpSlice.reducer;
